import React, { useState, useEffect, memo } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';

import { ProductGrid } from '../product-grid/ProductGrid';
import StickyControls from '../sticky-controls/StickyControls';
import Loading from '../loading/Loading';
import Pagination from '../pagination/Pagination';
import AppliedFilters from '../facets/applied-filters/AppliedFilters';
import { useAnchorCardContext } from '../../context/AnchorCardContext';
import { useFacetContext } from '../../context/FacetContext';
import { Context as SharedFacetContext } from '../facets/facet-context';
import { useProductContext } from '../../context/ProductContext';
import styles from './ProductListing.module.scss';
import { useProductCardSize } from '../../hooks/useProductCardSize';

export const getCategory = (breadcrumbs) => {
  if (breadcrumbs && breadcrumbs[breadcrumbs.length - 1]) {
    return breadcrumbs[breadcrumbs.length - 1]?.displayName || '';
  }
  return '';
};

const ProductListing = memo((props) => {
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingChunk, setIsLoadingChunk] = useState(false);
  const [isChunkLoadError, setIsChunkLoadError] = useState(false);

  const { isLargeCardSize } = useProductCardSize();
  const { anchorCard } = useAnchorCardContext();

  const { publicRuntimeConfig: features } = getConfig();

  const facetContext = useFacetContext();
  const { baseFacetId, applied, isPrimaryFacetInDiscoResponse, primaryFacet } =
    facetContext;
  const sharedFacetContext = {
    filterCombination: applied.filterCombination,
    filterCombinationNvals: applied.filterCombinationNvals,
  };

  const { pagesAvailable } = useProductContext();

  const router = useRouter();

  const { sortBy, page } = router.query;

  useEffect(() => {
    router.beforePopState(({ as }) => {
      const split = as.startsWith('/browse') ? '?' : '&';

      const currentPath = as.split(split)[0];
      const newPath = router.asPath.split(split)[0];

      if (currentPath === newPath) {
        // SSR reload for back button when the url are the same
        router.replace(as);
      }

      return true; // allow the popstate
    });

    const { events } = router;

    const handleRouteChangeStart = (url, { shallow }) => {
      if (!shallow) {
        setIsLoadingPage(true);
      }
    };
    const handleRouteChangeComplete = (url, { shallow }) => {
      if (!shallow) {
        setIsLoadingPage(false);
      }
    };

    events.on('routeChangeStart', handleRouteChangeStart);
    events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      events.off('routeChangeStart', handleRouteChangeStart);
      events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  });

  useEffect(() => {
    if (anchorCard) {
      document
        .querySelector(`[data-product-id="${anchorCard}"]`)
        .scrollIntoView();
    }
  }, [anchorCard, isLargeCardSize]);

  const {
    country,
    currency,
    handlePrimaryFacetClick,
    handleDisplayM2MProductSelector,
    handleDisplayQuickAdd,
    handleFilterDrawerClick,
    isHeaderPeeping,
    onFetchQuickViewData,
  } = props;

  return (
    <React.Fragment>
      {isLoadingPage && <Loading />}
      <SharedFacetContext.Provider value={sharedFacetContext}>
        <AppliedFilters
          baseFacetId={baseFacetId}
          className={'mobile-facets'}
          grid
          mobile
          numberOfFiltersToShowClear={features.numberOfFiltersToShowClear}
        />
      </SharedFacetContext.Provider>

      {isPrimaryFacetInDiscoResponse ? (
        <SecondaryButton
          data-test="filters-button"
          className={styles.primaryFacet}
          onClick={handlePrimaryFacetClick}
        >
          {primaryFacet.displayName}
        </SecondaryButton>
      ) : null}

      <StickyControls
        current={page}
        isHeaderPeeping={isHeaderPeeping}
        pages={pagesAvailable}
        sortBy={sortBy}
        handleFilterDrawerClick={handleFilterDrawerClick}
      />

      <div
        id="main-content"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex="0"
        aria-label="Product listing page results"
        className={styles.mainContent}
      >
        <ProductGrid
          country={country}
          currency={currency}
          fetchQuickViewData={onFetchQuickViewData}
          handleDisplayM2MProductSelector={handleDisplayM2MProductSelector}
          handleDisplayQuickAdd={handleDisplayQuickAdd}
          isLoadingChunk={isLoadingChunk}
          isLoadingPage={isLoadingPage}
          setIsLoadingChunk={setIsLoadingChunk}
          isChunkLoadError={isChunkLoadError}
          setIsChunkLoadError={setIsChunkLoadError}
        />
      </div>

      {!isLoadingChunk && !isChunkLoadError && (
        <Pagination
          className="c-pagination--bottom"
          current={parseInt(page, 10)}
          isFooter
          pages={pagesAvailable}
        />
      )}
    </React.Fragment>
  );
});

ProductListing.displayName = 'ProductListing';

ProductListing.defaultProps = {
  country: 'GB',
  currency: 'gbp',
  onFetchQuickViewData: () => null,
};

ProductListing.propTypes = {
  country: PropTypes.string,
  currency: PropTypes.string,
  onFetchQuickViewData: PropTypes.func,
};

export default ProductListing;
